import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

/**
 * Microsoft Clarity project ID, i.e. `environment.clarityProjectId`. Wired per-project in `app.config.ts`.
 * Set only in `environment.prod.ts`, so staging and local builds never record. Empty = Clarity off.
 */
export const Clarity_PROJECT_ID = new InjectionToken<string>('Clarity_PROJECT_ID');

// Clarity's global: a queue stub until its tag script replaces it with the real function.
type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    clarity?: ClarityFn;
  }
}

const SCRIPT_ELEMENT_ID = 'ms-clarity-tag';
const TAG_URL = 'https://www.clarity.ms/tag/';

// Clarity project IDs are short lowercase alphanumerics. Anything else never reaches the script URL.
const PROJECT_ID_PATTERN = /^[a-z0-9]+$/i;

/**
 * Microsoft Clarity session recording (OMNI-3598). `init()` runs once at startup from each app's
 * `provideAppInitializer` and adds Clarity's tag script, the same as Clarity's own embed snippet.
 *
 * Clarity follows SPA route changes by itself, so there is no page-view tracking here. No identify or
 * custom tags are sent, and masking is set to Strict in the Clarity dashboard.
 *
 * The script loads async and `init()` never throws, so Clarity can't hold up or break app startup.
 */
@Injectable({ providedIn: 'root' })
export class ClarityService {
  constructor(@Optional() @Inject(Clarity_PROJECT_ID) private readonly projectId?: string | null) {}

  /** A valid project ID is set, so `init()` loads Clarity. */
  get isConfigured(): boolean {
    return !!this.projectId && PROJECT_ID_PATTERN.test(this.projectId);
  }

  /** Add Clarity's tag script once. No-op if unconfigured or Clarity is already on the page. */
  init(): void {
    if (!this.isConfigured) {
      return;
    }

    try {
      if (window.clarity || document.getElementById(SCRIPT_ELEMENT_ID)) {
        return;
      }

      // Calls made before the tag loads are queued for it, as in Clarity's snippet.
      const stub: ClarityFn = (...args: unknown[]) => {
        (stub.q = stub.q || []).push(args);
      };
      window.clarity = stub;

      const script = document.createElement('script');
      script.id = SCRIPT_ELEMENT_ID;
      script.async = true;
      script.src = TAG_URL + this.projectId;
      document.head.appendChild(script);
    } catch (err) {
      console.error(err);
    }
  }
}
